import { supabase } from './supabase';

export interface SubscriptionDetails {
  subscriptionTier: 'free' | 'premium';
  taskLimit: number;
  currentTaskCount: number;
  subscriptionExpiresAt: string | null;
  percentageUsed: number;
  isWithinLimit: boolean;
  daysRemaining: number | null;
}

// Helper function to add retry capability
async function fetchWithRetry<T>(
  fetchFn: () => Promise<T>,
  maxRetries = 3,
  delay = 1000
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error) {
      lastError = error;
      console.log(`Attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      // Exponential backoff
      delay *= 2;
    }
  }
  
  throw lastError;
}

export async function getUserSubscription(): Promise<SubscriptionDetails | null> {
  try {
    // Add session validation as the first step
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Session error:', sessionError);
      return getFallbackSubscription();
    }
    
    if (!sessionData.session) {
      console.log('No active session found, using fallback subscription data');
      return getFallbackSubscription();
    }
    
    // Use retry mechanism for user data fetch
    const { data: userData, error: userError } = await fetchWithRetry(
      () => supabase.auth.getUser(),
      3,
      1000
    );
    
    if (userError || !userData.user) {
      console.error('User data error:', userError);
      return getFallbackSubscription();
    }
    
    // Get todos count with retry
    const todosResult = await fetchWithRetry(
      async () => {
        const result = await supabase
          .from('todos')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userData.user.id);
        return result;
      },
      3,
      1000
    );
    const todosCount = (todosResult as any).count;
    const todosError = (todosResult as any).error;
    
    if (todosError) {
      console.error('Todos count error:', todosError);
      return getFallbackSubscription();
    }
    
    // Get template todos count with retry
    const templatesResult = await fetchWithRetry(
      async () => {
        const result = await supabase
          .from('project_templates')
          .select('id')
          .eq('user_id', userData.user.id);
        return result;
      },
      3,
      1000
    );
    const templates = (templatesResult as any).data;
    const templatesError = (templatesResult as any).error;
    
    if (templatesError) {
      console.error('Templates error:', templatesError);
      return getFallbackSubscription();
    }
    
    let templateTodosCount = 0;
    if (templates && templates.length > 0) {
      const templateIds = templates.map((t: any) => t.id);
      const templateTodosResult = await fetchWithRetry(
        async () => {
          const result = await supabase
            .from('template_todos')
            .select('*', { count: 'exact', head: true })
            .in('template_id', templateIds);
          return result;
        },
        3,
        1000
      );
      const count = (templateTodosResult as any).count;
      const templateTodosError = (templateTodosResult as any).error;
      
      if (templateTodosError) {
        console.error('Template todos count error:', templateTodosError);
        return getFallbackSubscription();
      }
      
      templateTodosCount = count || 0;
    }
    
    // Subscription state lives in app_metadata (writable only by the service role)
    const metadata = userData.user?.app_metadata || {};
    const subscriptionTier = metadata.subscription_tier || 'free';
    const taskLimit = metadata.task_limit || (subscriptionTier === 'premium' ? 1000 : 50);
    const subscriptionExpiresAt = metadata.subscription_expires_at || null;
    
    // Calculate total sticky note count
    const currentTaskCount = (todosCount || 0) + templateTodosCount;
    
    // Calculate percentage used
    const percentageUsed = Math.min(
      Math.round((currentTaskCount / taskLimit) * 100),
      100
    );
    
    // Calculate days remaining for premium subscription
    let daysRemaining = null;
    if (subscriptionTier === 'premium' && subscriptionExpiresAt) {
      const expiresAt = new Date(subscriptionExpiresAt);
      const now = new Date();
      const diffTime = expiresAt.getTime() - now.getTime();
      daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      daysRemaining = Math.max(0, daysRemaining);
    }
    
    return {
      subscriptionTier: subscriptionTier as 'free' | 'premium',
      taskLimit,
      currentTaskCount,
      subscriptionExpiresAt,
      percentageUsed,
      isWithinLimit: currentTaskCount < taskLimit,
      daysRemaining
    };
  } catch (error) {
    console.error('Error fetching subscription details:', error);
    return getFallbackSubscription();
  }
}

// Provide fallback subscription data when fetch fails
function getFallbackSubscription(): SubscriptionDetails {
  return {
    subscriptionTier: 'free',
    taskLimit: 50,
    currentTaskCount: 0,
    subscriptionExpiresAt: null,
    percentageUsed: 0,
    isWithinLimit: true,
    daysRemaining: null
  };
}

// Get Polar customer with cancel_at_period_end
export async function getPolarCustomer() {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError || !userData.user) {
      console.error('User error:', userError);
      return null;
    }

    // Direct query to polar_customers table including cancel_at_period_end
    const { data, error } = await supabase
      .from('polar_customers')
      .select('polar_customer_id, subscription_id, subscription_status, cancel_at_period_end')
      .eq('user_id', userData.user.id)
      .maybeSingle();

    if (error) {
      console.error('Error getting Polar customer:', error);
      return null;
    }

    // If no customer record found, return null silently (this is expected for new users)
    if (!data) {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching Polar customer:', error);
    return null;
  }
}

// Function to create Polar customer portal session
export async function createPolarPortalSession(userId: string, returnUrl?: string): Promise<string | null> {
  try {
    console.log('Creating Polar portal session for user:', userId);
    console.log('Return URL:', returnUrl);

    const { data: isTestUser } = await supabase.rpc('get_is_test_user');
    const edgeFunction = isTestUser ? 'polar-test' : 'polar';

    const { data, error } = await supabase.functions.invoke(edgeFunction, {
      body: {
        action: 'create-customer-portal',
        userId,
        returnUrl: returnUrl || window.location.origin
      }
    });

    if (error) {
      console.error('Polar function error:', error);
      throw error;
    }

    console.log('Portal session response:', data);

    return data?.url || null;
  } catch (error) {
    console.error('Error creating Polar portal session:', error);
    throw error;
  }
}

// Function to manually sync subscription status when webhooks fail
export async function syncSubscriptionStatus(): Promise<boolean> {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error('User not authenticated');
    }
    
    console.log('Syncing subscription status for user:', userData.user.id);

    const { data: isTestUser } = await supabase.rpc('get_is_test_user');
    const { data, error } = await supabase.functions.invoke(isTestUser ? 'polar-test' : 'polar', {
      body: {
        action: 'sync-subscription-status',
        userId: userData.user.id
      }
    });
    
    if (error) {
      console.error('Sync error:', error);
      throw error;
    }
    
    console.log('Sync result:', data);
    return data?.success || false;
  } catch (error) {
    console.error('Error syncing subscription status:', error);
    throw error;
  }
}

// Function to cancel a Polar subscription
export async function cancelPolarSubscription(subscriptionId: string): Promise<boolean> {
  try {
    const { data: isTestUser } = await supabase.rpc('get_is_test_user');
    const { data, error } = await supabase.functions.invoke(isTestUser ? 'polar-test' : 'polar', {
      body: {
        action: 'cancel-subscription',
        subscriptionId
      }
    });

    if (error) {
      console.error('Cancel subscription error:', error);
      throw error;
    }

    console.log('Cancel subscription result:', data);
    return data?.success || false;
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
}

// Function to immediately downgrade to free tier
export async function downgradeToFree(): Promise<boolean> {
  try {
    const { data: isTestUser } = await supabase.rpc('get_is_test_user');
    const { data, error } = await supabase.functions.invoke(isTestUser ? 'polar-test' : 'polar', {
      body: {
        action: 'downgrade-to-free'
      }
    });
    
    if (error) {
      console.error('Downgrade to free error:', error);
      throw error;
    }
    
    console.log('Downgrade to free result:', data);
    return data?.success || false;
  } catch (error) {
    console.error('Error downgrading to free:', error);
    throw error;
  }
}

// Function to check Polar checkout status
export async function checkPolarCheckout(checkoutId: string) {
  try {
    const { data: isTestUser } = await supabase.rpc('get_is_test_user');
    const { data, error } = await supabase.functions.invoke(isTestUser ? 'polar-test' : 'polar', {
      body: {
        action: 'get-checkout',
        checkoutId
      }
    });

    if (error) throw error;

    return data?.checkout || null;
  } catch (error) {
    console.error('Error checking Polar checkout:', error);
    return null;
  }
}